// TriggerChips — multi-select chip strip for tagging a dose with the
// reasons that drove it. The server now accepts any short string;
// `TRIGGER_PRESETS` is the seed list rendered first, and a "+ Add"
// affordance lets the user pin a custom tag ("after work calls",
// "Sunday mornings") that persists per-device and surfaces next time.
//
// Visual: filled-tonal chips (--color-bg-sunken ground, no border).
// Collapsed-by-default so the routine save flow stays fast.
// Prioritized: an optional `prioritized` array (e.g. from time-of-day
// heuristic) renders 2-3 chips in a top row at full size.

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { haptics } from '../../lib/haptics';

// Trigger tags are now free-form strings. We keep the type alias for
// callers that still want the rename — but it widens to any string.
export type TriggerTag = string;

const PRESETS: { value: string; label: string }[] = [
  { value: 'stress', label: 'Stress' },
  { value: 'boredom', label: 'Boredom' },
  { value: 'social', label: 'Social' },
  { value: 'pain', label: 'Pain' },
  { value: 'sleep', label: 'Sleep' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'habit', label: 'Habit' },
  { value: 'craving', label: 'Craving' },
  { value: 'other', label: 'Other' },
];

const CUSTOM_TAGS_KEY = 'qk.customTriggers.v1';
const CUSTOM_TAGS_MAX = 12;
const TAG_VALIDATE_RE = /^[\p{L}\p{N}][\p{L}\p{N} \-_'./&,]{0,39}$/u;

function readCustomTags(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_TAGS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, CUSTOM_TAGS_MAX);
  } catch {
    return [];
  }
}

function writeCustomTags(tags: string[]): void {
  try {
    window.localStorage.setItem(CUSTOM_TAGS_KEY, JSON.stringify(tags.slice(0, CUSTOM_TAGS_MAX)));
  } catch {
    // private mode / quota — best effort
  }
}

function labelFor(value: string): string {
  const preset = PRESETS.find((p) => p.value === value);
  if (preset) return preset.label;
  // Title-case the first char so "after work" → "After work"; preserve
  // the rest of the user's input verbatim (don't fight casing they
  // chose intentionally like "M-F mornings").
  return value.charAt(0).toUpperCase() + value.slice(1);
}

interface TriggerChipsProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Soft UI cap (server enforces hard cap of 5). */
  max?: number;
  /** If true, picker starts collapsed. Defaults to true. */
  defaultCollapsed?: boolean;
  /** Optional preset values to render at full size in the priority row. */
  prioritized?: readonly string[];
}

export function TriggerChips({
  value,
  onChange,
  max = 5,
  defaultCollapsed = true,
  prioritized,
}: TriggerChipsProps) {
  const [expanded, setExpanded] = useState(!defaultCollapsed || value.length > 0);
  const [customTags, setCustomTags] = useState<string[]>(() => readCustomTags());
  const [addingCustom, setAddingCustom] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  // Mirror selected-but-not-stored custom tags into the persistent list
  // so a user who types "M-F mornings" once sees it as a chip on the
  // next dose without having to re-type.
  useEffect(() => {
    const allKnown = new Set([...PRESETS.map((p) => p.value), ...customTags]);
    const newCustoms = value.filter((v) => !allKnown.has(v));
    if (newCustoms.length > 0) {
      const merged = Array.from(new Set([...newCustoms, ...customTags])).slice(0, CUSTOM_TAGS_MAX);
      setCustomTags(merged);
      writeCustomTags(merged);
    }
  }, [value, customTags]);

  function toggle(tag: string) {
    if (value.includes(tag)) {
      onChange(value.filter((t) => t !== tag));
      haptics.confirm();
    } else if (value.length < max) {
      onChange([...value, tag]);
      haptics.confirm();
    } else {
      haptics.warn();
    }
  }

  function removeCustom(tag: string) {
    const next = customTags.filter((t) => t !== tag);
    setCustomTags(next);
    writeCustomTags(next);
    // Also deselect if currently selected.
    if (value.includes(tag)) onChange(value.filter((v) => v !== tag));
  }

  function commitCustomDraft() {
    const trimmed = draft.trim();
    if (!trimmed) {
      setAddingCustom(false);
      setDraft('');
      setDraftError(null);
      return;
    }
    if (!TAG_VALIDATE_RE.test(trimmed)) {
      setDraftError('Letters, numbers, spaces, and basic punctuation only.');
      return;
    }
    // Dedupe against presets and existing customs (case-insensitive).
    const lower = trimmed.toLowerCase();
    const presetHit = PRESETS.find((p) => p.value.toLowerCase() === lower);
    if (presetHit) {
      toggle(presetHit.value);
      setAddingCustom(false);
      setDraft('');
      setDraftError(null);
      return;
    }
    const existing = customTags.find((t) => t.toLowerCase() === lower);
    const tag = existing ?? trimmed;
    if (!existing) {
      const merged = [tag, ...customTags].slice(0, CUSTOM_TAGS_MAX);
      setCustomTags(merged);
      writeCustomTags(merged);
    }
    if (!value.includes(tag) && value.length < max) {
      onChange([...value, tag]);
      haptics.confirm();
    }
    setAddingCustom(false);
    setDraft('');
    setDraftError(null);
  }

  const allValues = useMemo(() => {
    return [...PRESETS.map((p) => ({ value: p.value, label: p.label, custom: false })), ...customTags.map((t) => ({ value: t, label: labelFor(t), custom: true }))];
  }, [customTags]);

  if (!expanded) {
    const summary = value.length === 0 ? 'optional' : `${value.length} selected`;
    return (
      <div className="field">
        <button
          type="button"
          className="triggers-collapsed"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
        >
          <span className="triggers-collapsed-label">Triggers</span>
          <span className="triggers-collapsed-summary">· {summary}</span>
          <span className="triggers-collapsed-spacer" />
          <span className="triggers-collapsed-cta">+ Add</span>
          <Icon icon={ChevronDown} size={16} />
        </button>
      </div>
    );
  }

  const priSet = new Set(prioritized ?? []);
  const top = prioritized && prioritized.length > 0
    ? allValues.filter((t) => priSet.has(t.value)).sort(
        (a, b) => prioritized.indexOf(a.value) - prioritized.indexOf(b.value),
      )
    : [];
  const rest = top.length > 0 ? allValues.filter((t) => !priSet.has(t.value)) : allValues;

  return (
    <div className="field">
      <div className="triggers-header">
        <span className="field-label">
          Trigger {value.length > 0 ? `· ${value.length}` : '(optional)'}
        </span>
        <button
          type="button"
          className="triggers-collapse-btn"
          onClick={() => setExpanded(false)}
          aria-expanded={true}
          aria-label="Collapse triggers"
        >
          <Icon icon={ChevronUp} size={16} />
        </button>
      </div>
      {top.length > 0 ? (
        <div className="triggers-row triggers-row-priority">
          {top.map((t) => (
            <TriggerChip
              key={t.value}
              tag={t}
              on={value.includes(t.value)}
              onToggle={toggle}
              onRemoveCustom={t.custom ? removeCustom : undefined}
              disabled={!value.includes(t.value) && value.length >= max}
            />
          ))}
        </div>
      ) : null}
      <div className={`triggers-row ${top.length > 0 ? 'triggers-row-rest' : ''}`}>
        {rest.map((t) => (
          <TriggerChip
            key={t.value}
            tag={t}
            on={value.includes(t.value)}
            onToggle={toggle}
            onRemoveCustom={t.custom ? removeCustom : undefined}
            disabled={!value.includes(t.value) && value.length >= max}
          />
        ))}
        {!addingCustom ? (
          <button
            type="button"
            className="trigger-chip trigger-chip-add"
            onClick={() => setAddingCustom(true)}
            aria-label="Add a custom trigger"
          >
            <Icon icon={Plus} size={14} />
            <span>Add custom</span>
          </button>
        ) : (
          <span className="trigger-chip-input">
            <input
              type="text"
              autoFocus
              value={draft}
              maxLength={40}
              onChange={(e) => {
                setDraft(e.target.value);
                setDraftError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitCustomDraft();
                } else if (e.key === 'Escape') {
                  setAddingCustom(false);
                  setDraft('');
                  setDraftError(null);
                }
              }}
              onBlur={() => commitCustomDraft()}
              placeholder="e.g. after work calls"
              aria-label="Custom trigger name"
            />
          </span>
        )}
      </div>
      {draftError ? (
        <p className="field-error" role="alert" style={{ marginBlockStart: 'var(--space-1)' }}>
          {draftError}
        </p>
      ) : null}
    </div>
  );
}

interface TriggerChipProps {
  tag: { value: string; label: string; custom?: boolean };
  on: boolean;
  onToggle: (tag: string) => void;
  onRemoveCustom?: (tag: string) => void;
  disabled?: boolean;
}

function TriggerChip({ tag, on, onToggle, onRemoveCustom, disabled }: TriggerChipProps) {
  return (
    <span
      className="trigger-chip-wrap"
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      <button
        type="button"
        className="trigger-chip"
        data-on={on}
        aria-pressed={on}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) {
            haptics.warn();
            return;
          }
          onToggle(tag.value);
        }}
      >
        {tag.label}
      </button>
      {onRemoveCustom ? (
        <button
          type="button"
          className="trigger-chip-remove"
          aria-label={`Remove "${tag.label}" from your custom tags`}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveCustom(tag.value);
          }}
        >
          <Icon icon={X} size={12} />
        </button>
      ) : null}
    </span>
  );
}
